import { c as _c } from "react/compiler-runtime";
import type { DragEndEvent } from '@dnd-kit/core';
import { DndContext } from '@dnd-kit/core';
import { arrayMove, SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import type { ChoiceButtonStyle, ErrorDetail, VaultKind } from '@onefootprint/request-types/dashboard';
import { InlineAlert, type TemplateInputProps, useConfirmationDialog } from '@onefootprint/ui';
import { cx } from 'class-variance-authority';
import { useEffect, useRef, useState } from 'react';
import { FormProvider, useFieldArray, useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { BifrostFrame, PreviewPane } from '@/components/playbook-builder/components/shared/node-preview';
import useDirty from '@/components/playbook-builder/hooks/use-dirty';
import useMobileLayout from '@/hooks/use-mobile-layout';
import AddContentDropdown from './components/add-content-dropdown';
import DraggableQuestion from './components/draggable-question';
import PageSettings, { type PageSection } from './components/page-settings';
import QuestionPreview from './components/question-preview';
import QuestionSettings, { type QuestionSettingsRef } from './components/question-settings';
import QuestionContainer from './components/questions/question-container';
import TemplatedText from './components/templated-text';
import useAutoButtonValues from './hooks/use-auto-button-values';
import useAutoChoiceIdentifier from './hooks/use-auto-choice-identifier';
import { SubjectProvider } from './hooks/use-subject';
import type { CustomQuestionsFormData } from './types';
import { findFirstChoiceError } from './utils/choice-errors';
import { getConditionUpdates } from './utils/condition-cleanup';
import { conditionsToDataIdentifiers, conditionsToQuestionIds } from './utils/condition-references';
type CustomQuestionsFormProps = {
  defaultValues?: Partial<CustomQuestionsFormData>;
  onSubmit: (data: CustomQuestionsFormData) => void;
  autocompleteItems?: TemplateInputProps['autocompleteItems'];
  errors?: ErrorDetail[];
  canEdit?: boolean;
  subject: VaultKind;
};

// Spans, not Buttons: the card sits inside the drawer's form, where a real button would submit it,
// and each one sits inside the button that selects it. A link gets a row as tall as a button, as
// PageShell gives a stacked link at runtime.
const PreviewCta = t0 => {
  const $ = _c(3);
  const {
    variant,
    text
  } = t0;
  let t1;
  if ($[0] !== text || $[1] !== variant) {
    t1 = variant === "link" ? <span className="flex h-10 items-center justify-center"><span className="fp-link-button fp-custom-appearance text-label-2">{text}</span></span> : <span className="fp-button fp-custom-appearance text-label-2" data-variant={variant} data-size="large" data-full-width="true">{text}</span>;
    $[0] = text;
    $[1] = variant;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  return t1;
};
const PlaybookCustomQuestions = t0 => {
  const $ = _c(8);
  let props;
  let subject;
  if ($[0] !== t0) {
    ({
      subject,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = props;
    $[2] = subject;
  } else {
    props = $[1];
    subject = $[2];
  }
  let t1;
  if ($[3] !== props) {
    t1 = <CustomQuestionsForm {...props} />;
    $[3] = props;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== subject || $[6] !== t1) {
    t2 = <SubjectProvider value={subject}>{t1}</SubjectProvider>;
    $[5] = subject;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
};
const CustomQuestionsForm = t0 => {
  const $ = _c(206);
  const {
    defaultValues,
    onSubmit,
    autocompleteItems,
    errors,
    canEdit: t1
  } = t0;
  const canEdit = t1 === undefined ? false : t1;
  const {
    t
  } = useTranslation("playbook-custom-questions");
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      keyPrefix: "nodePreview"
    };
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  const {
    t: tPreview
  } = useTranslation("playbook-builder", t2);
  const confirm = useConfirmationDialog();
  const {
    toggle
  } = useDirty();
  const isMobile = useMobileLayout();
  const [selectedQuestionIndex, setSelectedQuestionIndex] = useState(null);
  const [selectedButtonId, setSelectedButtonId] = useState(null);
  let t3;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      basic: true,
      buttons: true,
      confirmation: true
    };
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  const [openSections, setOpenSections] = useState(t3);
  let t4;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {};
    $[2] = t4;
  } else {
    t4 = $[2];
  }
  const [openButtonIds, setOpenButtonIds] = useState(t4);
  let t5;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = (section, open) => setOpenSections(current => ({
      ...current,
      [section]: open
    }));
    $[3] = t5;
  } else {
    t5 = $[3];
  }
  const setSectionOpen = t5;
  let t6;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = (id, open_0) => setOpenButtonIds(current_0 => ({
      ...current_0,
      [id]: open_0
    }));
    $[4] = t6;
  } else {
    t6 = $[4];
  }
  const setButtonOpen = t6;
  const previewAreaRef = useRef(null);
  const pageHeaderRef = useRef(null);
  const settingsAreaRef = useRef(null);
  const questionSettingsRef = useRef(null);
  const t7 = !canEdit;
  const t8 = defaultValues?.title || "";
  const t9 = defaultValues?.subtitle || "";
  let t10;
  if ($[5] !== defaultValues?.questions) {
    t10 = defaultValues?.questions || [];
    $[5] = defaultValues?.questions;
    $[6] = t10;
  } else {
    t10 = $[6];
  }
  let t11;
  if ($[7] !== t10) {
    t11 = conditionsToQuestionIds(t10);
    $[7] = t10;
    $[8] = t11;
  } else {
    t11 = $[8];
  }
  const t12 = defaultValues?.confirmationMode || "confirm";
  let t13;
  if ($[9] !== defaultValues?.choice) {
    t13 = defaultValues?.choice ?? {
      saveValue: false,
      dataIdentifier: "",
      buttons: []
    };
    $[9] = defaultValues?.choice;
    $[10] = t13;
  } else {
    t13 = $[10];
  }
  let t14;
  if ($[11] !== t11 || $[12] !== t12 || $[13] !== t13 || $[14] !== t8 || $[15] !== t9) {
    t14 = {
      title: t8,
      subtitle: t9,
      questions: t11,
      confirmationMode: t12,
      choice: t13
    };
    $[11] = t11;
    $[12] = t12;
    $[13] = t13;
    $[14] = t8;
    $[15] = t9;
    $[16] = t14;
  } else {
    t14 = $[16];
  }
  let t15;
  if ($[17] !== t14 || $[18] !== t7) {
    t15 = {
      mode: "onChange",
      disabled: t7,
      defaultValues: t14
    };
    $[17] = t14;
    $[18] = t7;
    $[19] = t15;
  } else {
    t15 = $[19];
  }
  const formMethods = useForm(t15);
  const {
    control,
    trigger,
    formState,
    setError
  } = formMethods;
  let t16;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = ["title", "subtitle", "choice.buttons"];
    $[20] = t16;
  } else {
    t16 = $[20];
  }
  let t17;
  if ($[21] !== control) {
    t17 = {
      control,
      name: t16
    };
    $[21] = control;
    $[22] = t17;
  } else {
    t17 = $[22];
  }
  const [title, subtitle, buttons] = useWatch(t17);
  useAutoChoiceIdentifier(formMethods);
  useAutoButtonValues(formMethods);
  let t18;
  let t19;
  if ($[23] !== formState.isDirty || $[24] !== toggle) {
    t18 = () => {
      toggle("custom-questions-form", formState.isDirty);
    };
    t19 = [formState.isDirty, toggle];
    $[23] = formState.isDirty;
    $[24] = toggle;
    $[25] = t18;
    $[26] = t19;
  } else {
    t18 = $[25];
    t19 = $[26];
  }
  useEffect(t18, t19);
  let t20;
  if ($[27] !== control) {
    t20 = {
      control,
      name: "questions",
      keyName: "_fieldId"
    };
    $[27] = control;
    $[28] = t20;
  } else {
    t20 = $[28];
  }
  const {
    fields: questions,
    append,
    move,
    replace
  } = useFieldArray(t20);
  let t21;
  if ($[29] !== trigger) {
    t21 = async index => {
      if (index !== null && index !== "header") {
        await trigger(`questions.${index}`);
      }
    };
    $[29] = trigger;
    $[30] = t21;
  } else {
    t21 = $[30];
  }
  const validateQuestion = t21;
  let t22;
  if ($[31] !== append || $[32] !== questions.length || $[33] !== selectedQuestionIndex || $[34] !== t || $[35] !== validateQuestion) {
    t22 = async contentType => {
      const generateId = _temp;
      const id_0 = generateId();
      await validateQuestion(selectedQuestionIndex);
      setSelectedButtonId(null);
      if (contentType === "text_block") {
        append({
          id: id_0,
          kind: "text_block" as const,
          content: ""
        });
        return setSelectedQuestionIndex(questions.length);
      }
      if (contentType === "short_text") {
        append({
          id: id_0,
          kind: "short_text" as const,
          label: "",
          dataIdentifier: "",
          required: true,
          format: "any" as const
        });
        return setSelectedQuestionIndex(questions.length);
      }
      if (contentType === "long_text") {
        append({
          id: id_0,
          kind: "long_text" as const,
          label: "",
          dataIdentifier: "",
          required: true
        });
        setSelectedQuestionIndex(questions.length);
        return;
      }
      if (contentType === "number") {
        append({
          id: id_0,
          kind: "number" as const,
          label: "",
          dataIdentifier: "",
          required: true,
          numberFormat: "any"
        });
        return setSelectedQuestionIndex(questions.length);
      }
      if (contentType === "dropdown") {
        append({
          id: id_0,
          kind: "dropdown" as const,
          label: "",
          dataIdentifier: "",
          required: true,
          options: [{
            value: "option-1",
            label: t(_temp2)
          }, {
            value: "option-2",
            label: t(_temp3)
          }],
          includeOther: false
        });
        return setSelectedQuestionIndex(questions.length);
      }
      if (contentType === "multi-select") {
        append({
          id: id_0,
          kind: "checkboxes" as const,
          label: "",
          dataIdentifier: "",
          required: true,
          options: [{
            value: "option-1",
            label: t(_temp4)
          }, {
            value: "option-2",
            label: t(_temp5)
          }]
        });
        return setSelectedQuestionIndex(questions.length);
      }
      if (contentType === "checkbox") {
        append({
          id: id_0,
          kind: "checkbox" as const,
          label: "",
          dataIdentifier: "",
          required: true
        });
        return setSelectedQuestionIndex(questions.length);
      }
      if (contentType === "multiple_choice") {
        append({
          id: id_0,
          kind: "multiple_choice" as const,
          label: "",
          dataIdentifier: "",
          required: true,
          options: [{
            value: "option-1",
            label: t(_temp6)
          }, {
            value: "option-2",
            label: t(_temp7)
          }]
        });
        return setSelectedQuestionIndex(questions.length);
      }
      if (contentType === "address") {
        append({
          id: id_0,
          kind: "address" as const,
          label: "",
          dataIdentifier: "",
          required: true,
          allowPoBox: false
        });
        return setSelectedQuestionIndex(questions.length);
      }
    };
    $[31] = append;
    $[32] = questions.length;
    $[33] = selectedQuestionIndex;
    $[34] = t;
    $[35] = validateQuestion;
    $[36] = t22;
  } else {
    t22 = $[36];
  }
  const handleAddContent = t22;
  let t23;
  if ($[37] !== t) {
    t23 = t(_temp8);
    $[37] = t;
    $[38] = t23;
  } else {
    t23 = $[38];
  }
  const stockLabel = t23;
  let t24;
  if ($[39] !== stockLabel) {
    t24 = choice => {
      const [only, ...rest] = choice.buttons;
      return !choice.saveValue && rest.length === 0 && only?.label.trim() === stockLabel;
    };
    $[39] = stockLabel;
    $[40] = t24;
  } else {
    t24 = $[40];
  }
  const isStockContinue = t24;
  let t25;
  if ($[41] !== formMethods || $[42] !== isStockContinue || $[43] !== onSubmit || $[44] !== setError || $[45] !== t) {
    let t26;
    if ($[47] !== isStockContinue || $[48] !== onSubmit || $[49] !== setError || $[50] !== t) {
      t26 = submitted => {
        const data = isStockContinue(submitted.choice) ? {
          ...submitted,
          choice: {
            ...submitted.choice,
            buttons: []
          }
        } : submitted;
        const unfinished = data.questions.findIndex(_temp9);
        if (unfinished !== -1) {
          setError(`questions.${unfinished}.dataIdentifier`, {
            type: "required",
            message: t(_temp10)
          });
          setSelectedButtonId(null);
          setSelectedQuestionIndex(unfinished);
          return;
        }
        const choiceError = findFirstChoiceError(data.choice);
        if (choiceError) {
          setSelectedQuestionIndex(null);
          setSectionOpen("buttons", true);
          const message = t($_7 => $_7.buttons.errors[choiceError.key]);
          if (choiceError.field === "dataIdentifier") {
            setError("choice.dataIdentifier", {
              message
            });
          } else {
            const failing = data.choice.buttons[choiceError.index];
            if (failing) {
              setButtonOpen(failing.id, true);
            }
            setError(`choice.buttons.${choiceError.index}.${choiceError.field}`, {
              message
            });
          }
          return;
        }
        onSubmit?.({
          ...data,
          questions: conditionsToDataIdentifiers(data.questions)
        });
      };
      $[47] = isStockContinue;
      $[48] = onSubmit;
      $[49] = setError;
      $[50] = t;
      $[51] = t26;
    } else {
      t26 = $[51];
    }
    t25 = formMethods.handleSubmit(t26);
    $[41] = formMethods;
    $[42] = isStockContinue;
    $[43] = onSubmit;
    $[44] = setError;
    $[45] = t;
    $[46] = t25;
  } else {
    t25 = $[46];
  }
  const handleSubmit = t25;
  let t26;
  if ($[52] !== formMethods) {
    t26 = next => formMethods.setValue("choice.buttons", next, {
      shouldDirty: true
    });
    $[52] = formMethods;
    $[53] = t26;
  } else {
    t26 = $[53];
  }
  const setButtons = t26;
  const newButton = _temp11;
  let t27;
  if ($[54] !== selectedQuestionIndex || $[55] !== validateQuestion) {
    t27 = async id_1 => {
      await validateQuestion(selectedQuestionIndex);
      setSelectedQuestionIndex(null);
      setSelectedButtonId(id_1);
      setSectionOpen("buttons", true);
      setButtonOpen(id_1, true);
    };
    $[54] = selectedQuestionIndex;
    $[55] = validateQuestion;
    $[56] = t27;
  } else {
    t27 = $[56];
  }
  const selectButton = t27;
  let t28;
  if ($[57] !== selectButton || $[58] !== selectedButtonId) {
    t28 = id_2 => {
      if (selectedButtonId === id_2) {
        setSelectedButtonId(null);
      } else {
        selectButton(id_2);
      }
    };
    $[57] = selectButton;
    $[58] = selectedButtonId;
    $[59] = t28;
  } else {
    t28 = $[59];
  }
  const toggleSelectedButton = t28;
  let t29;
  if ($[60] !== formMethods || $[61] !== stockLabel) {
    t29 = () => {
      const stock = newButton(stockLabel, "primary");
      formMethods.setValue("choice.buttons", [stock]);
      return stock.id;
    };
    $[60] = formMethods;
    $[61] = stockLabel;
    $[62] = t29;
  } else {
    t29 = $[62];
  }
  const materialiseStockButton = t29;
  let t30;
  if ($[63] !== materialiseStockButton || $[64] !== selectButton) {
    t30 = () => selectButton(materialiseStockButton());
    $[63] = materialiseStockButton;
    $[64] = selectButton;
    $[65] = t30;
  } else {
    t30 = $[65];
  }
  const handleSelectStockButton = t30;
  let t31;
  if ($[66] !== materialiseStockButton) {
    t31 = () => setButtonOpen(materialiseStockButton(), true);
    $[66] = materialiseStockButton;
    $[67] = t31;
  } else {
    t31 = $[67];
  }
  const handleOpenStockButton = t31;
  let t32;
  if ($[68] !== buttons || $[69] !== setButtons || $[70] !== stockLabel) {
    t32 = () => {
      const added = newButton();
      setButtons(buttons.length === 0 ? [newButton(stockLabel, "primary"), added] : [...buttons, added]);
      setSectionOpen("buttons", true);
      setButtonOpen(added.id, true);
    };
    $[68] = buttons;
    $[69] = setButtons;
    $[70] = stockLabel;
    $[71] = t32;
  } else {
    t32 = $[71];
  }
  const handleAddButton = t32;
  let t33;
  if ($[72] !== buttons.length || $[73] !== formMethods || $[74] !== setButtons || $[75] !== stockLabel) {
    t33 = saveValue => {
      if (!saveValue) {
        return;
      }
      if (formMethods.getValues("confirmationMode") === "skip") {
        formMethods.setValue("confirmationMode", "confirm", {
          shouldDirty: true
        });
      }
      if (buttons.length > 0) {
        return;
      }
      const stock_0 = newButton(stockLabel, "primary");
      setButtons([stock_0]);
      setButtonOpen(stock_0.id, true);
    };
    $[72] = buttons.length;
    $[73] = formMethods;
    $[74] = setButtons;
    $[75] = stockLabel;
    $[76] = t33;
  } else {
    t33 = $[76];
  }
  const handleSaveValueChange = t33;
  let t34;
  if ($[77] !== buttons || $[78] !== formMethods || $[79] !== selectedButtonId || $[80] !== setButtons) {
    t34 = id_3 => {
      setButtons(buttons.filter(button => button.id !== id_3));
      formMethods.clearErrors("choice");
      if (selectedButtonId === id_3) {
        setSelectedButtonId(null);
      }
    };
    $[77] = buttons;
    $[78] = formMethods;
    $[79] = selectedButtonId;
    $[80] = setButtons;
    $[81] = t34;
  } else {
    t34 = $[81];
  }
  const handleDeleteButton = t34;
  let t35;
  if ($[82] !== buttons || $[83] !== confirm || $[84] !== formMethods || $[85] !== handleDeleteButton || $[86] !== t) {
    t35 = id_4 => {
      const value = formMethods.getValues("choice.saveValue") ? buttons.find(button_0 => button_0.id === id_4)?.value.trim() : undefined;
      confirm.open({
        title: t(_temp12),
        description: value ? t(_temp13, {
          value
        }) : t(_temp14),
        variant: "destructive",
        primaryButton: {
          label: t(_temp15),
          onClick: () => handleDeleteButton(id_4)
        },
        secondaryButton: {
          label: t(_temp16)
        }
      });
    };
    $[82] = buttons;
    $[83] = confirm;
    $[84] = formMethods;
    $[85] = handleDeleteButton;
    $[86] = t;
    $[87] = t35;
  } else {
    t35 = $[87];
  }
  const confirmDeleteButton = t35;
  let t36;
  if ($[88] !== confirmDeleteButton || $[89] !== formState.dirtyFields || $[90] !== handleDeleteButton) {
    t36 = (id_5, index_0) => {
      if (formState.dirtyFields.choice?.buttons?.[index_0]) {
        confirmDeleteButton(id_5);
      } else {
        handleDeleteButton(id_5);
      }
    };
    $[88] = confirmDeleteButton;
    $[89] = formState.dirtyFields;
    $[90] = handleDeleteButton;
    $[91] = t36;
  } else {
    t36 = $[91];
  }
  const handleDeletePanelButton = t36;
  let t37;
  if ($[92] !== selectedButtonId) {
    t37 = open_1 => {
      if (!open_1) {
        return;
      }
      if (selectedButtonId) {
        setButtonOpen(selectedButtonId, false);
      }
      setSelectedButtonId(null);
      setSelectedQuestionIndex(_temp17);
    };
    $[92] = selectedButtonId;
    $[93] = t37;
  } else {
    t37 = $[93];
  }
  const handleAddContentOpenChange = t37;
  let t38;
  if ($[94] !== buttons || $[95] !== setButtons) {
    t38 = t39 => {
      const {
        active,
        over
      } = t39;
      if (!over || active.id === over.id) {
        return;
      }
      const from = buttons.findIndex(button_1 => button_1.id === active.id);
      const to = buttons.findIndex(button_2 => button_2.id === over.id);
      setButtons(arrayMove(buttons, from, to));
    };
    $[94] = buttons;
    $[95] = setButtons;
    $[96] = t38;
  } else {
    t38 = $[96];
  }
  const handleButtonDragEnd = t38;
  let t39;
  if ($[97] !== formMethods) {
    t39 = excludeIdentifiers => {
      const currentQuestions = formMethods.getValues("questions");
      const updates = getConditionUpdates(currentQuestions, excludeIdentifiers);
      updates.forEach(t40 => {
        const {
          index: index_1,
          cleanedCondition
        } = t40;
        formMethods.setValue(`questions.${index_1}.condition`, cleanedCondition, {
          shouldDirty: true
        });
      });
    };
    $[97] = formMethods;
    $[98] = t39;
  } else {
    t39 = $[98];
  }
  const applyConditionUpdates = t39;
  let t40;
  if ($[99] !== applyConditionUpdates || $[100] !== confirm || $[101] !== formMethods || $[102] !== questions || $[103] !== replace || $[104] !== selectedQuestionIndex || $[105] !== t) {
    t40 = index_2 => {
      const deletedId = questions[index_2]?.id;
      confirm.open({
        title: t(_temp18),
        description: t(_temp19),
        variant: "destructive",
        primaryButton: {
          label: t(_temp20),
          onClick: () => {
            const currentQuestions_0 = formMethods.getValues("questions");
            const newQuestions = currentQuestions_0.filter((_, i) => i !== index_2);
            replace(newQuestions);
            applyConditionUpdates(deletedId ? new Set([deletedId]) : undefined);
            if (selectedQuestionIndex === index_2) {
              setSelectedQuestionIndex(null);
            } else {
              if (selectedQuestionIndex !== null && selectedQuestionIndex !== "header" && selectedQuestionIndex > index_2) {
                setSelectedQuestionIndex(selectedQuestionIndex - 1);
              }
            }
          }
        },
        secondaryButton: {
          label: t(_temp21)
        }
      });
    };
    $[99] = applyConditionUpdates;
    $[100] = confirm;
    $[101] = formMethods;
    $[102] = questions;
    $[103] = replace;
    $[104] = selectedQuestionIndex;
    $[105] = t;
    $[106] = t40;
  } else {
    t40 = $[106];
  }
  const handleDeleteQuestion = t40;
  let t41;
  if ($[107] !== applyConditionUpdates || $[108] !== move || $[109] !== questions || $[110] !== selectedQuestionIndex) {
    t41 = event => {
      const {
        active: active_0,
        over: over_0
      } = event;
      if (over_0 && active_0.id !== over_0.id) {
        const oldIndex = questions.findIndex(item => item.id === active_0.id);
        const newIndex = questions.findIndex(item_0 => item_0.id === over_0.id);
        move(oldIndex, newIndex);
        applyConditionUpdates();
        if (selectedQuestionIndex !== null && selectedQuestionIndex !== "header") {
          if (selectedQuestionIndex === oldIndex) {
            setSelectedQuestionIndex(newIndex);
          } else {
            if (oldIndex < selectedQuestionIndex && newIndex >= selectedQuestionIndex) {
              setSelectedQuestionIndex(selectedQuestionIndex - 1);
            } else {
              if (oldIndex > selectedQuestionIndex && newIndex <= selectedQuestionIndex) {
                setSelectedQuestionIndex(selectedQuestionIndex + 1);
              }
            }
          }
        }
      }
    };
    $[107] = applyConditionUpdates;
    $[108] = move;
    $[109] = questions;
    $[110] = selectedQuestionIndex;
    $[111] = t41;
  } else {
    t41 = $[111];
  }
  const handleDragEnd = t41;
  let t42;
  if ($[112] !== applyConditionUpdates || $[113] !== formMethods || $[114] !== questions || $[115] !== replace || $[116] !== selectedQuestionIndex) {
    t42 = () => {
      if (selectedQuestionIndex === null || selectedQuestionIndex === "header") {
        return;
      }
      const deletedId_0 = questions[selectedQuestionIndex]?.id;
      const currentQuestions_1 = formMethods.getValues("questions");
      const newQuestions_0 = currentQuestions_1.filter((__0, i_0) => i_0 !== selectedQuestionIndex);
      replace(newQuestions_0);
      applyConditionUpdates(deletedId_0 ? new Set([deletedId_0]) : undefined);
      setSelectedQuestionIndex(null);
    };
    $[112] = applyConditionUpdates;
    $[113] = formMethods;
    $[114] = questions;
    $[115] = replace;
    $[116] = selectedQuestionIndex;
    $[117] = t42;
  } else {
    t42 = $[117];
  }
  const handleDeleteSelectedQuestion = t42;
  let t43;
  if ($[118] !== isMobile || $[119] !== selectedQuestionIndex || $[120] !== validateQuestion) {
    t43 = async index_3 => {
      await validateQuestion(selectedQuestionIndex);
      setSelectedButtonId(null);
      if (selectedQuestionIndex === index_3) {
        setSelectedQuestionIndex(null);
      } else {
        setSelectedQuestionIndex(index_3);
        if (index_3 === "header") {
          setSectionOpen("basic", true);
        }
        if (isMobile) {
          requestAnimationFrame(() => settingsAreaRef.current?.scrollIntoView({
            behavior: "smooth",
            block: "start"
          }));
        }
      }
    };
    $[118] = isMobile;
    $[119] = selectedQuestionIndex;
    $[120] = validateQuestion;
    $[121] = t43;
  } else {
    t43 = $[121];
  }
  const toggleSelectedQuestionIndex = t43;
  const isPageHeaderSelected = selectedQuestionIndex === "header";
  const isQuestionSelected = typeof selectedQuestionIndex === "number";
  let t44;
  if ($[122] !== errors) {
    t44 = errors && errors.length > 0 && <div className="mb-4 flex flex-col gap-2">{errors.map(_temp22)}</div>;
    $[122] = errors;
    $[123] = t44;
  } else {
    t44 = $[123];
  }
  const t45 = !canEdit;
  const t46 = !isPageHeaderSelected;
  let t47;
  if ($[124] !== isPageHeaderSelected || $[125] !== t46) {
    t47 = cx("flex flex-col gap-2 w-[calc(100%-48px)] rounded border-2 transition-colors py-3 cursor-pointer", {
      "border-accent": isPageHeaderSelected,
      "border-transparent hover:border-tertiary": t46
    });
    $[124] = isPageHeaderSelected;
    $[125] = t46;
    $[126] = t47;
  } else {
    t47 = $[126];
  }
  let t48;
  if ($[127] !== toggleSelectedQuestionIndex) {
    t48 = () => toggleSelectedQuestionIndex("header");
    $[127] = toggleSelectedQuestionIndex;
    $[128] = t48;
  } else {
    t48 = $[128];
  }
  let t49;
  if ($[129] !== subtitle || $[130] !== tPreview || $[131] !== title) {
    t49 = title ? <><div className="text-heading-3 text-primary text-center w-full"><TemplatedText value={title} /></div>{subtitle && <div className="text-body-2 text-secondary text-center w-full"><TemplatedText value={subtitle} /></div>}</> : <><div className="text-heading-3 text-tertiary text-center w-full">{tPreview(_temp23)}</div><div className="text-body-2 text-tertiary text-center w-full">{tPreview(_temp24)}</div></>;
    $[129] = subtitle;
    $[130] = tPreview;
    $[131] = title;
    $[132] = t49;
  } else {
    t49 = $[132];
  }
  let t50;
  if ($[133] !== t45 || $[134] !== t47 || $[135] !== t48 || $[136] !== t49) {
    t50 = <button ref={pageHeaderRef} type="button" disabled={t45} className={t47} onClick={t48}>{t49}</button>;
    $[133] = t45;
    $[134] = t47;
    $[135] = t48;
    $[136] = t49;
    $[137] = t50;
  } else {
    t50 = $[137];
  }
  let t51;
  if ($[138] !== canEdit || $[139] !== handleDeleteQuestion || $[140] !== handleDragEnd || $[141] !== questions || $[142] !== selectedQuestionIndex || $[143] !== toggleSelectedQuestionIndex) {
    t51 = questions.length > 0 && <DndContext onDragEnd={handleDragEnd}><SortableContext items={questions.map(_temp25)} strategy={verticalListSortingStrategy}><div className="flex flex-col gap-1 mb-6">{questions.map((question_0, index_4) => <div key={question_0.id} className="group"><DraggableQuestion id={question_0.id} disableDrag={questions.length === 1} canEdit={canEdit}><QuestionPreview question={question_0} questionIndex={index_4} isSelected={selectedQuestionIndex === index_4} onClick={() => toggleSelectedQuestionIndex(index_4)} onDelete={() => handleDeleteQuestion(index_4)} canEdit={canEdit} /></DraggableQuestion></div>)}</div></SortableContext></DndContext>;
    $[138] = canEdit;
    $[139] = handleDeleteQuestion;
    $[140] = handleDragEnd;
    $[141] = questions;
    $[142] = selectedQuestionIndex;
    $[143] = toggleSelectedQuestionIndex;
    $[144] = t51;
  } else {
    t51 = $[144];
  }
  let t52;
  if ($[145] !== canEdit || $[146] !== handleAddContent || $[147] !== handleAddContentOpenChange) {
    t52 = canEdit && <AddContentDropdown onSelect={handleAddContent} onOpenChange={handleAddContentOpenChange} />;
    $[145] = canEdit;
    $[146] = handleAddContent;
    $[147] = handleAddContentOpenChange;
    $[148] = t52;
  } else {
    t52 = $[148];
  }
  let t53;
  if ($[149] !== t51 || $[150] !== t52) {
    t53 = <div className="px-6 py-5 w-full" ref={previewAreaRef}>{t51}{t52}</div>;
    $[149] = t51;
    $[150] = t52;
    $[151] = t53;
  } else {
    t53 = $[151];
  }
  let t54;
  if ($[152] !== t50 || $[153] !== t53) {
    t54 = <div className="flex flex-col items-center justify-center">{t50}{t53}</div>;
    $[152] = t50;
    $[153] = t53;
    $[154] = t54;
  } else {
    t54 = $[154];
  }
  let t55;
  if ($[155] !== buttons.length || $[156] !== canEdit || $[157] !== stockLabel) {
    t55 = buttons.length === 0 && !canEdit && <PreviewCta variant="primary" text={stockLabel} />;
    $[155] = buttons.length;
    $[156] = canEdit;
    $[157] = stockLabel;
    $[158] = t55;
  } else {
    t55 = $[158];
  }
  let t56;
  if ($[159] !== buttons.length || $[160] !== canEdit || $[161] !== handleSelectStockButton || $[162] !== stockLabel) {
    t56 = buttons.length === 0 && canEdit && <QuestionContainer isSelected={false} onClick={handleSelectStockButton} canEdit={true} flush={true}><PreviewCta variant="primary" text={stockLabel} /></QuestionContainer>;
    $[159] = buttons.length;
    $[160] = canEdit;
    $[161] = handleSelectStockButton;
    $[162] = stockLabel;
    $[163] = t56;
  } else {
    t56 = $[163];
  }
  let t57;
  if ($[164] !== buttons || $[165] !== canEdit || $[166] !== confirmDeleteButton || $[167] !== formState.errors || $[168] !== handleButtonDragEnd || $[169] !== selectedButtonId || $[170] !== t || $[171] !== toggleSelectedButton) {
    t57 = buttons.length > 0 && <DndContext onDragEnd={handleButtonDragEnd}><SortableContext items={buttons.map(_temp26)} strategy={verticalListSortingStrategy}>{buttons.map((button_4, index_5) => <DraggableQuestion key={button_4.id} id={button_4.id} disableDrag={buttons.length === 1} canEdit={canEdit}><QuestionContainer isSelected={selectedButtonId === button_4.id} onClick={() => toggleSelectedButton(button_4.id)} onDelete={button_4.style === "primary" ? undefined : () => confirmDeleteButton(button_4.id)} hasError={!!formState.errors.choice?.buttons?.[index_5]} canEdit={canEdit} deleteLabel={t(_temp27)} flush={true}><PreviewCta variant={button_4.style} text={button_4.label.trim() || t(_temp28, {
              index: index_5 + 1
            })} /></QuestionContainer></DraggableQuestion>)}</SortableContext></DndContext>;
    $[164] = buttons;
    $[165] = canEdit;
    $[166] = confirmDeleteButton;
    $[167] = formState.errors;
    $[168] = handleButtonDragEnd;
    $[169] = selectedButtonId;
    $[170] = t;
    $[171] = toggleSelectedButton;
    $[172] = t57;
  } else {
    t57 = $[172];
  }
  let t58;
  if ($[173] !== t55 || $[174] !== t56 || $[175] !== t57) {
    t58 = <div className="px-6 pb-6 flex flex-col gap-3">{t55}{t56}{t57}</div>;
    $[173] = t55;
    $[174] = t56;
    $[175] = t57;
    $[176] = t58;
  } else {
    t58 = $[176];
  }
  let t59;
  if ($[177] !== t54 || $[178] !== t58) {
    t59 = <PreviewPane fill={true}><BifrostFrame editable={true}>{t54}{t58}</BifrostFrame></PreviewPane>;
    $[177] = t54;
    $[178] = t58;
    $[179] = t59;
  } else {
    t59 = $[179];
  }
  let t60;
  if ($[180] !== t44 || $[181] !== t59) {
    t60 = <div className="shrink-0 flex flex-col">{t44}{t59}</div>;
    $[180] = t44;
    $[181] = t59;
    $[182] = t60;
  } else {
    t60 = $[182];
  }
  let t61;
  if ($[183] !== autocompleteItems || $[184] !== canEdit || $[185] !== handleAddButton || $[186] !== handleDeletePanelButton || $[187] !== handleDeleteSelectedQuestion || $[188] !== handleOpenStockButton || $[189] !== handleSaveValueChange || $[190] !== isPageHeaderSelected || $[191] !== isQuestionSelected || $[192] !== openButtonIds || $[193] !== openSections || $[194] !== selectedButtonId || $[195] !== selectedQuestionIndex) {
    t61 = <aside ref={settingsAreaRef} className="md:flex-1 md:min-w-0"><div className="overflow-hidden md:sticky md:top-24">{isQuestionSelected ? <QuestionSettings ref={questionSettingsRef} key={selectedQuestionIndex} questionIndex={selectedQuestionIndex} autocompleteItems={autocompleteItems} onDelete={handleDeleteSelectedQuestion} canEdit={canEdit} /> : <PageSettings autocompleteItems={autocompleteItems} canEdit={canEdit} focusTitle={isPageHeaderSelected} openSections={openSections} onSectionOpenChange={setSectionOpen} buttons={{
          selectedId: selectedButtonId,
          openIds: openButtonIds,
          onButtonOpenChange: setButtonOpen,
          onAdd: handleAddButton,
          onDelete: handleDeletePanelButton,
          onOpenStock: handleOpenStockButton,
          onSaveValueChange: handleSaveValueChange
        }} />}</div></aside>;
    $[183] = autocompleteItems;
    $[184] = canEdit;
    $[185] = handleAddButton;
    $[186] = handleDeletePanelButton;
    $[187] = handleDeleteSelectedQuestion;
    $[188] = handleOpenStockButton;
    $[189] = handleSaveValueChange;
    $[190] = isPageHeaderSelected;
    $[191] = isQuestionSelected;
    $[192] = openButtonIds;
    $[193] = openSections;
    $[194] = selectedButtonId;
    $[195] = selectedQuestionIndex;
    $[196] = t61;
  } else {
    t61 = $[196];
  }
  let t62;
  if ($[197] !== t60 || $[198] !== t61) {
    t62 = <div className="flex flex-col gap-4 w-full md:flex-row md:gap-6">{t60}{t61}</div>;
    $[197] = t60;
    $[198] = t61;
    $[199] = t62;
  } else {
    t62 = $[199];
  }
  let t63;
  if ($[200] !== handleSubmit || $[201] !== t62) {
    t63 = <form id="custom-questions-form" onSubmit={handleSubmit} aria-label="Custom questions form" className="pt-8 flex flex-col items-center md:px-2 md:pt-2 md:pb-6">{t62}</form>;
    $[200] = handleSubmit;
    $[201] = t62;
    $[202] = t63;
  } else {
    t63 = $[202];
  }
  let t64;
  if ($[203] !== formMethods || $[204] !== t63) {
    t64 = <FormProvider {...formMethods}>{t63}</FormProvider>;
    $[203] = formMethods;
    $[204] = t63;
    $[205] = t64;
  } else {
    t64 = $[205];
  }
  return t64;
};
export default PlaybookCustomQuestions;
function _temp() {
  return Math.random().toString(36).substring(2) + Date.now().toString(36);
}
function _temp2($) {
  return $.defaults.option1;
}
function _temp3($_0) {
  return $_0.defaults.option2;
}
function _temp4($_1) {
  return $_1.defaults.option1;
}
function _temp5($_2) {
  return $_2.defaults.option2;
}
function _temp6($_3) {
  return $_3.defaults.option1;
}
function _temp7($_4) {
  return $_4.defaults.option2;
}
function _temp8($_5) {
  return $_5.preview.cta;
}
function _temp9(question) {
  return question.kind !== "text_block" && !question.dataIdentifier.replace(/^custom\./, "");
}
function _temp10($_6) {
  return $_6.form.dataIdentifierRequired;
}
function _temp11(t0, t1) {
  const label = t0 === undefined ? "" : t0;
  const style = t1 === undefined ? "secondary" : t1;
  return {
    id: Math.random().toString(36).substring(2) + Date.now().toString(36),
    label,
    value: "",
    style
  };
}
function _temp12($_8) {
  return $_8.buttons.confirmDelete.title;
}
function _temp13($_9) {
  return $_9.buttons.confirmDelete.descriptionWithValue;
}
function _temp14($_10) {
  return $_10.buttons.confirmDelete.description;
}
function _temp15($_11) {
  return $_11.confirmDelete.delete;
}
function _temp16($_12) {
  return $_12.confirmDelete.cancel;
}
function _temp17(current_1) {
  return current_1 === "header" ? null : current_1;
}
function _temp18($_13) {
  return $_13.confirmDelete.title;
}
function _temp19($_14) {
  return $_14.confirmDelete.description;
}
function _temp20($_15) {
  return $_15.confirmDelete.delete;
}
function _temp21($_16) {
  return $_16.confirmDelete.cancel;
}
function _temp22(error) {
  return <InlineAlert key={error.message} variant="error">{error.message}</InlineAlert>;
}
function _temp23($_17) {
  return $_17.headerPlaceholder;
}
function _temp24($_18) {
  return $_18.subheaderPlaceholder;
}
function _temp25(q) {
  return q.id;
}
function _temp26(button_3) {
  return button_3.id;
}
function _temp27($_19) {
  return $_19.preview.deleteButton;
}
function _temp28($_20) {
  return $_20.buttons.button;
}